import { ref, readonly } from 'vue'
import logger from '@/utils/logger'

/**
 * Session Activity Composable for Olvidadme
 *
 * Implementing Activity Tracking Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-024: Client-side inactivity detection and automatic logout
 * Extracted from auth store for better modularization and reusability
 *
 * Security Features:
 * - Configurable inactivity timeout (default: 30 minutes)
 * - Multiple DOM event tracking for comprehensive activity detection
 * - Automatic cleanup to prevent memory leaks
 * - GDPR-compliant structured logging for audit trail
 * - Secure callback system for logout handling
 *
 * Legal Context:
 * Critical for Olvidadme legal platform where professionals handle sensitive
 * GDPR data and need automatic session protection during long processes.
 */
export function useSessionActivity() {
  // ==========================================
  // STATE - Activity tracking state
  // ==========================================

  const lastActivity = ref(null)
  const inactivityTimeout = ref(null)
  const activityListeners = ref([])
  const isTracking = ref(false)

  // Store current callback and config for timer resets
  const currentCallback = ref(null)
  const currentConfig = ref(null)

  // Default configuration (configurable)
  const defaultConfig = {
    inactivityPeriod: 30 * 60 * 1000, // 30 minutes in milliseconds
    events: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'],
    enableLogging: true
  }

  // ==========================================
  // ACTIVITY TRACKING CORE FUNCTIONS
  // ==========================================

  /**
   * Start tracking user activity for automatic logout
   * Implements comprehensive DOM event monitoring per ADR-024
   *
   * @param {Function} onInactivityCallback - Callback function to execute on inactivity timeout
   * @param {Object} config - Configuration options
   * @param {number} config.inactivityPeriod - Timeout period in milliseconds
   * @param {Array} config.events - DOM events to monitor
   * @param {boolean} config.enableLogging - Whether to enable structured logging
   * @param {string} config.userId - User ID for logging context
   */
  const startActivityTracking = (onInactivityCallback, config = {}) => {
    if (isTracking.value) {
      logger.warn('Activity tracking already started', {
        isTracking: isTracking.value
      })
      return
    }

    if (typeof onInactivityCallback !== 'function') {
      throw new Error('onInactivityCallback must be a function')
    }

    // Merge configuration with defaults
    const finalConfig = { ...defaultConfig, ...config }

    // Store callback and config for resets
    currentCallback.value = onInactivityCallback
    currentConfig.value = finalConfig

    // Update last activity timestamp
    lastActivity.value = Date.now()

    const trackActivity = () => {
      lastActivity.value = Date.now()
      resetInactivityTimer(onInactivityCallback, finalConfig)
    }

    // Add event listeners for activity detection
    finalConfig.events.forEach(event => {
      document.addEventListener(event, trackActivity, {
        passive: true, // Better performance for scroll/touch events
        capture: true   // Catch events in capture phase
      })
    })

    // Store listeners for cleanup
    activityListeners.value = finalConfig.events.map(event => ({
      event,
      trackActivity,
      options: { passive: true, capture: true }
    }))

    // Start inactivity timer
    resetInactivityTimer(onInactivityCallback, finalConfig)

    // Mark as actively tracking
    isTracking.value = true

    if (finalConfig.enableLogging) {
      logger.debug('Session activity tracking started', {
        userId: finalConfig.userId,
        inactivityPeriod: Math.round(finalConfig.inactivityPeriod / 1000 / 60), // minutes
        eventsCount: finalConfig.events.length,
        events: finalConfig.events
      })
    }
  }

  /**
   * Reset the inactivity timer
   * Creates new timeout for automatic logout on user inactivity
   *
   * @param {Function} onInactivityCallback - Callback function for inactivity timeout
   * @param {Object} config - Configuration with inactivityPeriod and logging settings
   */
  const resetInactivityTimer = (onInactivityCallback, config) => {
    // Clear existing timeout
    if (inactivityTimeout.value) {
      clearTimeout(inactivityTimeout.value)
    }

    // Create new timeout for inactivity detection
    inactivityTimeout.value = setTimeout(async () => {
      const inactivityDuration = Date.now() - lastActivity.value

      if (config.enableLogging) {
        logger.warn('User session expired due to inactivity', {
          userId: config.userId,
          lastActivity: lastActivity.value,
          inactivityDuration: Math.round(inactivityDuration / 1000 / 60), // minutes
          configuredTimeout: Math.round(config.inactivityPeriod / 1000 / 60) // minutes
        })
      }

      // Stop tracking before calling callback
      stopActivityTracking(config)

      // Execute inactivity callback (typically logout)
      try {
        await onInactivityCallback('inactivity_timeout')
      } catch (error) {
        if (config.enableLogging) {
          logger.error('Inactivity callback execution failed', {
            error: error.message,
            userId: config.userId
          })
        }
      }
    }, config.inactivityPeriod)
  }

  /**
   * Stop activity tracking and cleanup resources
   * Removes event listeners and clears timers to prevent memory leaks
   *
   * @param {Object} config - Configuration for logging
   */
  const stopActivityTracking = (config = {}) => {
    if (!isTracking.value) {
      return
    }

    // Remove all event listeners
    activityListeners.value.forEach(({ event, trackActivity, options }) => {
      document.removeEventListener(event, trackActivity, options)
    })

    // Clear listeners array
    activityListeners.value = []

    // Clear inactivity timeout
    if (inactivityTimeout.value) {
      clearTimeout(inactivityTimeout.value)
      inactivityTimeout.value = null
    }

    // Reset activity timestamp
    lastActivity.value = null

    // Clear stored references
    currentCallback.value = null
    currentConfig.value = null

    // Mark as not tracking
    isTracking.value = false

    if (config.enableLogging) {
      logger.debug('Session activity tracking stopped', {
        userId: config.userId,
        reason: 'manual_stop'
      })
    }
  }

  /**
   * Get current activity status and metrics
   * Useful for debugging and monitoring
   *
   * @returns {Object} Activity status information
   */
  const getActivityStatus = () => {
    return {
      isTracking: isTracking.value,
      lastActivity: lastActivity.value,
      timeSinceLastActivity: lastActivity.value ? Date.now() - lastActivity.value : null,
      activeListenersCount: activityListeners.value.length,
      hasActiveTimeout: !!inactivityTimeout.value
    }
  }

  /**
   * Manually update activity timestamp and reset timer
   * Useful for programmatic activity updates
   */
  const updateActivity = () => {
    if (isTracking.value && currentCallback.value && currentConfig.value) {
      lastActivity.value = Date.now()
      resetInactivityTimer(currentCallback.value, currentConfig.value)
    }
  }

  /**
   * Get time remaining until inactivity timeout
   * Useful for displaying countdown warnings
   *
   * @param {number} configuredTimeout - Configured timeout period in milliseconds
   * @returns {number} Milliseconds remaining until timeout (0 if past timeout)
   */
  const getTimeUntilTimeout = (configuredTimeout = defaultConfig.inactivityPeriod) => {
    if (!lastActivity.value || !isTracking.value) {
      return 0
    }

    const elapsed = Date.now() - lastActivity.value
    const remaining = configuredTimeout - elapsed

    return Math.max(0, remaining)
  }

  // ==========================================
  // COMPOSABLE RETURN INTERFACE
  // ==========================================

  return {
    // State (readonly)
    lastActivity: readonly(lastActivity),
    isTracking: readonly(isTracking),

    // Core functions
    startActivityTracking,
    stopActivityTracking,
    resetInactivityTimer,

    // Utility functions
    getActivityStatus,
    updateActivity,
    getTimeUntilTimeout,

    // Configuration defaults (for reference)
    defaultConfig: readonly(defaultConfig)
  }
}

/**
 * Factory function for creating pre-configured session activity composables
 * Useful for specific use cases with standard configurations
 *
 * @param {Object} preConfig - Pre-configured settings
 * @returns {Function} Configured useSessionActivity function
 */
export function createSessionActivityComposable(preConfig = {}) {
  return () => {
    const composable = useSessionActivity()

    // Override default config with pre-configuration
    const mergedConfig = { ...composable.defaultConfig, ...preConfig }

    return {
      ...composable,
      startActivityTracking: (callback, config = {}) => {
        return composable.startActivityTracking(callback, { ...mergedConfig, ...config })
      }
    }
  }
}

// Export default configuration for external reference
export const SESSION_ACTIVITY_CONFIG = {
  INACTIVITY_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  WARNING_THRESHOLD: 5 * 60 * 1000,   // 5 minutes before timeout
  EVENTS: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
}